import { cn } from '@/lib/tailwind-utils';
import { flexRender, Header } from '@tanstack/react-table';
import { getCommonPinningStyles, PinnedColumnsState } from './entity-table';
import { TableHeaderSorting } from './table-header-sorting';
import { TableHead as UITableHead } from '../ui/table';
import { ArrowLeftToLine, PinOff, Pin } from 'lucide-react';
import { Tooltip } from '../tooltip';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '@/auth/useSession';
import { isAirAdmin } from '@airreading/common';
import { useLocation } from '@tanstack/react-router';
import { useLocalStorage } from 'usehooks-ts';

type TableHeadProps<TData> = {
  header: Header<TData, unknown>;
  headerClassName?: string;
  tableKey?: string;
  autoManagedColumnIds?: string[];
};

export const TableHead = <TData,>(props: TableHeadProps<TData>) => {
  const { header, headerClassName, tableKey, autoManagedColumnIds } = props;
  const { column, id, isPlaceholder, getContext } = header;
  const { pathname } = useLocation();

  const { actingUser } = useSession();
  const isAdmin = isAirAdmin(actingUser);

  const [shouldShowPinIcon, setShouldShowPinIcon] = useState(false);
  const thRef = useRef<HTMLTableCellElement>(null);

  const isPinned = column.getIsPinned();
  const isAutoManaged = autoManagedColumnIds?.includes(id) ?? false;
  const [pinnedColumns, setPinnedColumns] = useLocalStorage<PinnedColumnsState>('pinnedColumns', {});

  const onPinnedColumnsChange = (pinned: 'left' | false) => {
    const key = tableKey ?? pathname;

    if (pinned === false) {
      setPinnedColumns((prev) => ({
        ...prev,
        [key]: prev[key].filter((item) => item[0] !== id),
      }));
      return;
    }

    setPinnedColumns((prev) => ({
      ...prev,
      [key]: [...(prev[key] ?? []), [id, pinned]],
    }));
  };

  const onPinClick = () => {
    if (isPinned) {
      column.pin(false);
      onPinnedColumnsChange(false);
      return;
    }

    if (!shouldShowPinIcon) {
      return;
    }

    // Capture the current rendered width so the column keeps its visual size when pinned.
    const renderedWidth = thRef.current?.offsetWidth;
    if (renderedWidth) {
      getContext().table.setColumnSizing((prev) => ({ ...prev, [column.id]: renderedWidth }));
    }

    column.pin('left');
    onPinnedColumnsChange('left');
  };

  useEffect(() => {
    const pinnedColumn = pinnedColumns[tableKey ?? pathname]?.find((item) => item[0] === id);

    if (!pinnedColumn) {
      return;
    }

    column.pin(pinnedColumn[1]);
  }, [pathname, column, id, pinnedColumns, tableKey]);

  return (
    <UITableHead
      ref={thRef}
      key={id}
      className={cn('whitespace-nowrap', headerClassName)}
      style={{ ...getCommonPinningStyles(column) }}
      onMouseEnter={() => setShouldShowPinIcon(true)}
      onMouseLeave={() => setShouldShowPinIcon(false)}
    >
      <div className="flex items-center gap-x-2">
        {/* Text + sort: clips when pinned so it never inflates the column width */}
        <div
          className={cn(
            'flex items-center gap-x-2 py-1',
            isPinned ? 'min-w-0 flex-1 overflow-hidden' : 'whitespace-nowrap',
          )}
        >
          {isPlaceholder ? null : flexRender(column.columnDef.header, getContext())}
          <TableHeaderSorting column={column} />
        </div>
        {/* Pin icon kept outside the overflow area so it is always fully visible */}
        {isAdmin && !isAutoManaged && (
          <div className="w-4 flex items-center flex-shrink-0">
            <button onClick={onPinClick} className="p-0 bg-transparent">
              {isPinned && !shouldShowPinIcon && <Pin size={16} />}
              {isPinned && shouldShowPinIcon && <Tooltip trigger={<PinOff size={16} />}>Unpin</Tooltip>}
              {!isPinned && shouldShowPinIcon && <Tooltip trigger={<ArrowLeftToLine size={16} />}>Pin column</Tooltip>}
            </button>
          </div>
        )}
      </div>
    </UITableHead>
  );
};
